import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PricePreviewRequest,
  PricePreviewResponse,
  PricingProfilePutInput,
  PricingProfileResponse,
  VehicleCatalogQuery,
  VehicleCatalogResponse,
  VehicleCatalogTrimDetail,
  WorkspaceVehicleInput,
  WorkspaceVehicleListItem,
  WorkspaceVehicleListResponse,
  WorkspaceVehicleUpdate,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export type VehiclePricingSource = 'catalog' | 'workspace';

// Account-level, not workspace-scoped (see server/src/modules/pricing/routes.ts:
// GET /api/v1/vehicle-catalog has no :workspaceId in its path and is gated by
// requireAuth only), so this hook takes no workspaceId for the request itself —
// it's only in the query key for cache namespacing consistency with the rest of
// this feature's hooks.
export function useVehicleCatalog(workspaceId: string | null, query: VehicleCatalogQuery & { enabled?: boolean }) {
  const { enabled, ...params } = query;
  return useQuery({
    queryKey: [workspaceId, 'vehicle-catalog', params],
    queryFn: () => api.get<VehicleCatalogResponse>('/api/v1/vehicle-catalog', { query: params }),
    enabled: enabled ?? true,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useWrapPricingProfile(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'pricing-profiles', 'wrap'],
    queryFn: () => api.get<PricingProfileResponse>(workspacePath(workspaceId!, '/pricing-profiles/wrap')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

// GET .../pricing-profiles/:service already accepts 'ppf' (routes.ts's
// serviceParam is a 4-way enum) - sibling hook rather than generalizing
// useWrapPricingProfile so existing wrap callers are untouched.
export function usePpfPricingProfile(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'pricing-profiles', 'ppf'],
    queryFn: () => api.get<PricingProfileResponse>(workspacePath(workspaceId!, '/pricing-profiles/ppf')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

// Sibling of usePpfPricingProfile - 'tint' is also accepted by the same
// 4-way serviceParam enum.
export function useTintPricingProfile(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'pricing-profiles', 'tint'],
    queryFn: () => api.get<PricingProfileResponse>(workspacePath(workspaceId!, '/pricing-profiles/tint')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

// Sibling of usePpfPricingProfile - 'ceramic' is also accepted by the same
// 4-way serviceParam enum.
export function useCeramicPricingProfile(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'pricing-profiles', 'ceramic'],
    queryFn: () => api.get<PricingProfileResponse>(workspacePath(workspaceId!, '/pricing-profiles/ceramic')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

// Authoritative double-check, called once when a line's configuration is
// finalized — not on every drag/keystroke. Live preview math during editing
// uses the imported pure engine directly (see VehicleAwareWrapLineBuilder).
export function usePricePreview(workspaceId: string | null, quoteId: string | undefined) {
  return useMutation({
    mutationFn: (input: PricePreviewRequest) =>
      api.post<PricePreviewResponse>(workspacePath(workspaceId!, `/quotes/${quoteId}/lines/price-preview`), input),
  });
}

export function useUpdateWrapPricingProfile(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PricingProfilePutInput) =>
      api.put<PricingProfileResponse>(workspacePath(workspaceId!, '/pricing-profiles/wrap'), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'pricing-profiles', 'wrap'] });
    },
  });
}

export function useCreateWorkspaceVehicle(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkspaceVehicleInput) => api.post<{ id: string }>(workspacePath(workspaceId!, '/pricing-vehicles'), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'pricing-vehicles'] });
    },
  });
}

// Lets VehiclePicker's search surface this workspace's own custom vehicles
// (created via "Vehicle not listed?") alongside the platform vehicle_catalog,
// so one isn't only ever reachable as a one-off entry on the quote line it
// was created for.
export function useWorkspaceVehicles(workspaceId: string | null, query: { q?: string; enabled?: boolean }) {
  const { enabled, ...params } = query;
  return useQuery({
    queryKey: [workspaceId, 'pricing-vehicles', params],
    queryFn: () => api.get<WorkspaceVehicleListResponse>(workspacePath(workspaceId!, '/pricing-vehicles'), { query: params }),
    enabled: (enabled ?? true) && !!workspaceId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// §34.1 - Settings Vehicles tab edits a 'custom' row's own partial/full
// disassembly minutes (or other fields) via this PATCH, sibling to
// useCreateWorkspaceVehicle above (same /pricing-vehicles resource).
export function useUpdateWorkspaceVehicle(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, input }: { vehicleId: string; input: WorkspaceVehicleUpdate }) =>
      api.patch<void>(workspacePath(workspaceId!, `/pricing-vehicles/${vehicleId}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'pricing-vehicles'] });
    },
  });
}

// Single-vehicle sibling of useWorkspaceVehicles above - lets a saved quote
// line's editor fully reconstruct the exact custom vehicle it picked (see
// VehiclePicker.tsx's vehicleSelectionFromWorkspaceVehicleItem).
export function useWorkspaceVehicleById(workspaceId: string | null, vehicleId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'pricing-vehicles', vehicleId],
    queryFn: () => api.get<WorkspaceVehicleListItem>(workspacePath(workspaceId!, `/pricing-vehicles/${vehicleId}`)),
    enabled: !!workspaceId && !!vehicleId,
  });
}

// Single catalog trim, resolved to this workspace's effective disassembly
// minutes - lets a saved quote line's editor fully reconstruct the exact
// catalog trim it picked (see VehiclePicker.tsx's vehicleSelectionFromTrimDetail).
export function useVehicleCatalogTrimDetail(workspaceId: string | null, trimId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'vehicle-catalog', 'trims', trimId],
    queryFn: () => api.get<VehicleCatalogTrimDetail>(workspacePath(workspaceId!, `/vehicle-catalog/trims/${trimId}`)),
    enabled: !!workspaceId && !!trimId,
  });
}
